<script>
    import { onMount } from "svelte";
    import { userStore, tweetStore } from './stores.js';
    import { HIGHLIGHT_COLOUR } from './constants.js';

    import * as d3 from "d3";

    export let data;
    export let nodeId;
    export let nodeGroup;
    export let nodeTitle;
    export let width;
    export let height;

    let networkGraph;

    onMount(() => {
        let chart = ForceGraph(data, {
        nodeId,
        nodeGroup,
        nodeTitle,
        width,
        height,
        });
        networkGraph.appendChild(chart);
});

    function ForceGraph({
    nodes, // an iterable of node objects (typically [{id}, …])
    links // an iterable of link objects (typically [{source, target}, …])
}, {
    nodeId = d => d.id, // given d in nodes, returns a unique identifier (string)
    nodeGroup, // given d in nodes, returns an (ordinal) value for color
    nodeGroups, // an array of ordinal values representing the node groups
    nodeTitle, // given d in nodes, a title string
    nodeFill = "currentColor", // node stroke fill (if not using a group color encoding)
    nodeStroke = "#4da1ff", // node stroke color
    nodeStrokeWidth = 1.5, // node stroke width, in pixels
    nodeStrokeOpacity = 0.7, // node stroke opacity
    nodeRadius = 5, // node radius, in pixels
    nodeStrength,
    linkSource = ({ source }) => source, // given d in links, returns a node identifier string
    linkTarget = ({ target }) => target, // given d in links, returns a node identifier string
    linkUrl = ({ url }) => url, // given d in links, returns a node identifier string
    linkStroke = "#999", // link stroke color
    linkStrokeOpacity = 0.6, // link stroke opacity
    linkStrokeWidth = 1.5, // given d in links, returns a stroke width in pixels
    linkStrokeLinecap = "round", // link stroke linecap
    linkStrength,
    colors = ['#7517f8', "#e323ff", "#02a4ff", "#7d40ff", "#s4da1ff", "#4dffdf"], // an array of color strings, for the node groups
    width = 640, // outer width, in pixels
    height = 400, // outer height, in pixels
    invalidation // when this promise resolves, stop the simulation
} = {}) {
    // Compute values.
    
    const N = d3.map(nodes, nodeId).map(intern);
    const LS = d3.map(links, linkSource).map(intern);
    const LT = d3.map(links, linkTarget).map(intern);
    const LU = d3.map(links, linkUrl).map(intern);
    const DEGREE = d3.map(nodes, d => d.degree);
    const NSMAX = d3.max(DEGREE);

    if (nodeTitle === undefined) nodeTitle = (_, i) => N[i];
    const T = nodeTitle == null ? null : d3.map(nodes, nodeTitle);
    const G = nodeGroup == null ? null : d3.map(nodes, nodeGroup).map(intern);
    const W = typeof linkStrokeWidth !== "function" ? null : d3.map(links, linkStrokeWidth);

    // Replace the input nodes and links with mutable objects for the simulation.
    nodes = d3.map(nodes, (_, i) => ({ id: N[i], radius: DEGREE[i] / NSMAX * 20 + nodeRadius }));
    links = d3.map(links, (_, i) => ({ source: LS[i], target: LT[i], url: LU[i] }));

    // Compute default domains.
    if (G && nodeGroups === undefined) nodeGroups = d3.sort(G);

    // Construct the scales.
    const color = nodeGroup == null ? null : d3.scaleOrdinal(nodeGroups, colors);

    // Construct the forces.
    const forceNode = d3.forceManyBody();
    const forceLink = d3.forceLink(links).id(({ index: i }) => N[i]);
    if (nodeStrength !== undefined) forceNode.strength(nodeStrength);
    if (linkStrength !== undefined) forceLink.strength(linkStrength);

    const simulation = d3.forceSimulation(nodes)
        .force("link", forceLink)
        .force("charge", forceNode)
        .force("x", d3.forceX())
        .force("y", d3.forceY())
        .on("tick", ticked);

    const svg = d3.create("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [-width / 2, -height / 2, width, height])
        .attr("style", "max-width: 100%; height: auto; height: intrinsic;");

    /* function to zoom */
    const handleZoom = (e) => g.attr('transform', e.transform);
    const zoom = d3.zoom().on('zoom', handleZoom);
    const g = svg.append('g');
    svg.call(zoom);

    const link = g.append("g")
        .attr("stroke", linkStroke)
        .attr("stroke-opacity", linkStrokeOpacity)
        .attr("stroke-width", typeof linkStrokeWidth !== "function" ? linkStrokeWidth : null)
        .attr("stroke-linecap", linkStrokeLinecap)
        .selectAll("line")
        .data(links)
        .join("line");

    if (W) link.attr("stroke-width", ({ index: i }) => W[i]);

    const node = g.append("g")
        .attr("fill", nodeFill)
        .attr("stroke", nodeStroke)
        .attr("stroke-opacity", nodeStrokeOpacity)
        .attr("stroke-width", nodeStrokeWidth)
        .selectAll("circle")
        .data(nodes)
        .join("circle")
        .attr("r", function (d) { return d.radius })
        .attr("class", "node")
        .attr("id", function (d) { return d.id })
        .attr("data-url", function (d) { return 'https://twitter.com/' + d.id })
        .call(drag(simulation))
        ;

    /* 
        Here we initilise and show the tooltip. Can edit the tooltip as you see fit
        https://atomiks.github.io/tippyjs/
    */

    let pause = false;
    node.on('mouseover', function (_, d) {
        if (pause) return;
        let username = this.id;
        let url = this.getAttribute("data-url");
        userStore.update(() => ({
            username: username,
            url: url,
        }));
        
        node.style('fill', null);
        d3.select(this).style('fill', HIGHLIGHT_COLOUR);
        var nodesToHighlight = links.map(function (e) { return e.source === d ? e.target : e.target === d ? e.source : 0 })
            .filter(function (d) { return d; });
        var linksToHighlight = links.map(function (e) { return e.source === d || e.target === d ? e : null })
            .filter(function (d) { return d; });

        node.filter(function (d) { return nodesToHighlight.indexOf(d) >= 0; })
            .style('fill', HIGHLIGHT_COLOUR)
            .style("stroke-width", nodeStrokeWidth * 2);
        link.filter(function (d) { return linksToHighlight.indexOf(d) >= 0; })
            .style('stroke', '#d62333')
            .style("stroke-width", typeof linkStrokeWidth !== "function" ? linkStrokeWidth * 2 : null);
    });
    node.on('mouseout', function (d) {
        if (pause) return;
        node.style('fill', null)
            .style("stroke-width", null);
        link.style('stroke', null)
            .style("stroke-width", typeof linkStrokeWidth !== "function" ? linkStrokeWidth * 2 : null);
    });

    node.on("click", function (d) {
        if (pause) {
            
            tweetStore.update(() => ({
                tweets: ['-'],
            }));
        } else {
            let username = this.id
            let tweets = links.map((e) => { if (e.source.id === username) return e.url })
            let tt = [...new Set(tweets.filter(Boolean))];
            if (tt.length > 0) {
                tweetStore.update(() => ({
                    tweets: tt,
                }));
            }
            else {
                tweetStore.update(() => ({
                    tweets: ['No tweets found for today'],
                }));
            }    
        }

        pause = !pause;
    })

    if (G) node.attr("fill", ({ index: i }) => color(G[i]));
    if (T) node.append("title").text(({ index: i }) => T[i]);

    // Handle invalidation.
    if (invalidation != null) invalidation.then(() => simulation.stop());

    function intern(value) {
        return value !== null && typeof value === "object" ? value.valueOf() : value;
    }

    function ticked() {
        link
            .attr("x1", d => d.source.x)
            .attr("y1", d => d.source.y)
            .attr("x2", d => d.target.x)
            .attr("y2", d => d.target.y);

        node
            .attr("cx", d => d.x)
            .attr("cy", d => d.y);
    }

    function drag(simulation) {
        function dragstarted(event) {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
        }

        function dragged(event) {
            event.subject.fx = event.x;
            event.subject.fy = event.y;
        }

        function dragended(event) {
            if (!event.active) simulation.alphaTarget(0);
            event.subject.fx = null;
            event.subject.fy = null;
        }

        return d3.drag()
            .on("start", dragstarted)
            .on("drag", dragged)
            .on("end", dragended);
    }

    return Object.assign(svg.node(), { scales: { color } });
}
</script>

<div bind:this={networkGraph} class="flex-grow" />